import styled from 'styled-components'
import {
  H5,
  P,
  Span,
  Strong,
} from '../../../shared_components/Library/Typeography'
import { Box } from '../../../shared_components/UI/Box'
import { Divider, Text } from './styles'
import AnimatedLoader from '../../../shared_components/AnimatedLoader'
import {
  useGetSablierWithdrawalsQuery,
  useGetTokensClaimHistoryQuery,
} from '../../../modules/staking/api'
import { BASE } from '../../../constants'
import {
  improvedAmountFromWei,
  useStakingContext,
} from '../../../contexts/StakingContext'
import { BackButton } from '../../../shared_components/Page'
import { useShouldShowForThreshold } from '../../../theme'

const streamIdsMaps = {
  '98': 'Investors',
  '99': 'Advisory',
  '100': 'Seed',
  undefined: 'ZAP airdrop',
  // etc
}

function ClaimHistory() {
  const { tokensClaimedFromRewards, tokensClaimedFromVesting } =
    useStakingContext()
  
  const isAtLeastMediumDisplaySize = useShouldShowForThreshold('medium')

  return (
    <Box dc g={16}>
      <Text>A summary of all your transactions</Text>

      <Box g={16}>
        <StatBox
          target="tokens"
          amount={Number(tokensClaimedFromRewards)}
          label="Total tokens claimed from Vaults and token staking"
        />
        <StatBox
          target="vaults"
          amount={Number(tokensClaimedFromVesting)}
          label="Total tokens claimed from claimed vaults"
        />
      </Box>

      <Box dc padT={16}>
        <TokensClaimHistory />
      </Box>
    </Box>
  )
}

export default ClaimHistory

function StatBox({
  target,
  amount,
  label,
}: {
  target: 'vaults' | 'tokens'
  amount: number | bigint
  label: string
}) {
  return (
    <StyledStatBox dc g={6} pad={16}>
      <Box g={6} ac>
        <img
          src={
            target === 'vaults' ? '/vault-icon-white.svg' : '/token-icon.svg'
          }
          alt="token icon"
        />
        <H5 s="L" fw="bolder" c="text-primary" uc ff="Proxima Nova">
          {amount.toLocaleString()}
        </H5>
      </Box>
      <Box fu aw>
        <P s="S" fw="regular" c="text-primary">
          {label}
        </P>
      </Box>
    </StyledStatBox>
  )
}

const StyledStatBox = styled(Box)`
  height: 94px;
  justify-content: center;
  align-items: flex-start;
  flex: 1 0 0;

  border-radius: var(--units-8, 8px);
  border: var(--units-1, 1px) solid var(--stroke-tertiary, #252525);
  background: var(--states-invert-alpha-16, rgba(0, 0, 0, 0.16));
`

const eventTypeMap = {
  staking_rewards_claimed: 'Staking',
  vested_tokens_from_vaults_claimed: 'Claimed Vaults',
  // others [zap airdrop, seed investment, advisory investment]
}

function TokensClaimHistory() {
  const { data: tokensData, isLoading: isLoadingTokens } =
    useGetTokensClaimHistoryQuery({ refetchOnMountOrArgChange: true })
  const { data: sablierData, isLoading: isLoadingSablier } =
    useGetSablierWithdrawalsQuery({ refetchOnMountOrArgChange: true })

  const isLoading = isLoadingTokens || isLoadingSablier
  if (isLoading) return <AnimatedLoader />

  const events = tokensData?.data
  const withdrawals = sablierData?.withdrawals

  if ((events?.length ?? 0) === 0) return null
  if ((withdrawals?.length ?? 0) === 0) return null

  return (
    <Box dc g={32}>
      <Box dc g={24}>
        <Box g={8}>
          <Box mw="266px">
            <Span c="text-secondary" s="M" fw="regular">
              Txn
            </Span>
          </Box>

          <Box mw="140px" jfe>
            <Span c="text-secondary" s="M" fw="regular">
              Source
            </Span>
          </Box>

          <Box mw="206px" jfe>
            <Span c="text-secondary" s="M" fw="regular">
              Amount
            </Span>
          </Box>
        </Box>
        <Box dc g={16}>
          {events?.map((event, index) => {
            const tokensClaimed1 = improvedAmountFromWei(
              event.metaData.standardPoolTokensClaimed,
            )
            const tokensClaimed2 = improvedAmountFromWei(
              event.metaData.bonusPoolTokensClaimed,
            )
            const amount = Math.floor(Number(tokensClaimed1 + tokensClaimed2))
            return (
              <>
                <ClaimRow
                  key={event.id}
                  createdAt={event.createdAt}
                  txHash={event.metaData.txHash}
                  source={eventTypeMap[event.type]}
                  amount={amount}
                />
                {events.length - 1 !== index && <Divider />}
              </>
            )
          })}
          {withdrawals.map((withdrawal, index) => {
            const amount = Math.floor(
              Number(improvedAmountFromWei(withdrawal.amount)),
            )
            const source = streamIdsMaps[withdrawal.streamId]
            return (
              <>
                <ClaimRow
                  key={withdrawal.txHash}
                  createdAt={withdrawal.timestamp}
                  txHash={withdrawal.txHash}
                  source={source}
                  amount={amount}
                />
                {withdrawals.length - 1 !== index && <Divider />}
              </>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}

function ClaimRow({
  createdAt,
  txHash,
  source,
  amount,
}: {
  createdAt: string
  txHash: string
  source: string
  amount: number
}) {
  return (
    <>
      <Box>
        <Box mw="266px">
          <Box dc>
            <Strong c="text-primary" s="L" fw="semibold">
              {new Date(createdAt).toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                hour12: false,
              })}
            </Strong>
            <a
              href={BASE.explorerUrl + '/tx/' + txHash}
              target="_blank"
              rel="noreferrer"
            >
              <Span c="text-brandPrimary" s="S" fw="regular" tdu>
                Transaction Hash
              </Span>
            </a>
          </Box>
        </Box>

        <Box mw="140px" jfe ac>
          <Span c="text-secondary" s="S" fw="regular">
            {source}
          </Span>
        </Box>

        <Box mw="206px" jfe ac>
          <Box dc afe>
            <Strong c="text-primary" s="L" fw="semibold">
              {amount.toLocaleString()} ZAP
            </Strong>
          </Box>
        </Box>
      </Box>
    </>
  )
}
